import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import Decimal from 'decimal.js';

import { getOrderDetail } from '../orderDetailActions';
import logo from '../../../../assets/images/logo_new.png';
import useAuthDataStore from '../../../../hooks/store/useAuthDataStore';
import { getRetailerBillingAddressSvc } from '../../../../services/retailer';
import { getRetailerInfoSvc } from '../../../../services/retailer';
import { getOrderDetailRetailerApi } from '../../../../api/order';
import { showCustomToast } from '../../../../controls/CustomToast';
import { formatDate, formatDateTime } from '../../../../utils/formatDate';
import './taxInvoicePage.scss';
import OrderPrice from '../../../../utils/OrderPrice';

const TaxInvoicePage = (props) => {
  const dispatch = useDispatch();
  const [billingInfo, setBillingInfo] = useState(null);
  const [orderDetail, setOrderDetail] = useState(null);
  const [abn, setAbn] = useState('');
  const { getRetailerId } = useAuthDataStore.getState();
  const retailerId = getRetailerId();
  const { orderId } = useParams();

  useEffect(() => {
    if (orderId) {
      getOrderDetailRetailerApi({ id: orderId }).then((res) => {
        const detail = Array.isArray(res.data) ? res.data[0] : res.data;
        setOrderDetail(detail);
      });

      getRetailerBillingAddressSvc(retailerId)
        .then((res) => {
          if (res.success) {
            setBillingInfo(res.data);
          } else {
            showCustomToast('Error, Please try again later.');
          }
        })

      getRetailerInfoSvc(retailerId)
        .then((res) => {
          if (res.success) {
            setAbn(res.data.rs_abn);
          } else {
            showCustomToast('Error, Please try again later.');
          }
        })
    }
  }, [orderId, retailerId]);

  useEffect(() => {
    if (orderDetail && billingInfo) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          window.print();
        }, 500);
      });
    }
  }, [orderDetail, billingInfo]);

  const formatCurrency = (amount) => {
    const decimalAmount = new Decimal(amount);
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(decimalAmount.toFixed(2));
  };

  const calculateGST = (amount) => {
    const gst = new Decimal(amount).div(11);
    return gst.toDecimalPlaces(2);
  };

  // A gst_excluded order has had its GST removed, except an NZ order before the GST cut-off.
  const isGSTRemoved = (order) =>
    !!order.gst_excluded &&
    (OrderPrice.isGSTExcluded(order) || OrderPrice.isNZGSTApplicable(order.country, order.order_date));

  // Such an order still carries GST on its processing fee, as an NZ order does after the GST cut-off.
  const hasFeeOnlyGST = (order) => isGSTRemoved(order) && Number(order.processing_fee) > 0;

  const getTotalGSTAmount = (order) => {
    if (OrderPrice.isNZGSTApplicable(order.country, order.order_date) || hasFeeOnlyGST(order)) {
      return calculateGST(order.processing_fee || 0);
    }
    const grandTotalInclGST = getGrandTotalInclGST(order);
    return calculateGST(grandTotalInclGST);
  };

  const getItemsGSTSum = (orders) => {
    let itemsGSTSum = new Decimal(0);
    orders.items.forEach((item) => {
      itemsGSTSum = itemsGSTSum.plus(calculateGST(item.sub_total_inc_shipping));
    });
    // Include surcharge GST so the rounding adjustment on the last item stays near-zero.
    if (!OrderPrice.isNZGSTApplicable(orders.country, orders.order_date) && orders.postage_surcharge > 0) {
      itemsGSTSum = itemsGSTSum.plus(calculateGST(orders.postage_surcharge));
    }

    if (orders.handling_fee > 0) {
      itemsGSTSum = itemsGSTSum.plus(calculateGST(orders.handling_fee));
    }
    if (orders.pallet_fee > 0) {
      itemsGSTSum = itemsGSTSum.plus(calculateGST(orders.pallet_fee));
    }
    if (orders.processing_fee > 0) {
      itemsGSTSum = itemsGSTSum.plus(calculateGST(orders.processing_fee));
    }
    return itemsGSTSum;
  };

  const getAdjustedGST = (itemAmount, isLastItem, order) => {
    const totalGSTAmount = getTotalGSTAmount(order);
    const itemsGSTSum = getItemsGSTSum(order);
    const gstDifference = totalGSTAmount.minus(itemsGSTSum);

    if (isLastItem && gstDifference.abs().gte(0.01)) {
      return calculateGST(itemAmount).plus(gstDifference);
    }
    return calculateGST(itemAmount);
  };

  const getGrandTotalInclGST = (order) => {
    const isNZ = OrderPrice.isNZGSTApplicable(order.country, order.order_date);
    const itemsSum = (order.items || []).reduce(
      (acc, item) => acc.plus(item.sub_total_inc_shipping || 0),
      new Decimal(0)
    );
    const surcharge = new Decimal(order.postage_surcharge || 0);
    const adjustedSurcharge = isNZ ? surcharge.div(1.1) : surcharge;
    const manualSum = itemsSum.plus(adjustedSurcharge);
    const dbTotal = new Decimal(order.total_amount || 0);
    // Fall back to manual line-item sum only when total_amount is absent/zero.
    // When total_amount is present it reflects the actual amount the customer paid
    // and must be used as-is, even if it legitimately differs from the item sum
    // (e.g. partial refunds, price adjustments). The printed Grand Total must
    // match what was actually charged.
    const reconciledTotal = dbTotal.isZero() ? manualSum : dbTotal;
    return reconciledTotal.plus(order.processing_fee || 0);
  };

  const getOrderItemGST = (order, isLastItem, itemAmount) => {
    // NZ GST must be evaluated first and always wins: NZ orders post-2025-09-15
    // render "Free" regardless of gst_excluded, and the gst_excluded branch below
    // is for the other countries, where back-office removed GST.
    if (OrderPrice.isNZGSTApplicable(order.country, order.order_date)) {
      return 'Free';
    }
    if (OrderPrice.isGSTExcluded(order)) {
      return 'Free';
    }
    return formatCurrency(getAdjustedGST(itemAmount, isLastItem, orderDetail));
  };

  if (
    !orderDetail ||
    !billingInfo ||
    !abn ||
    orderDetail.status === 'unpaid' ||
    orderDetail.status === 'cancelled'
  ) {
    return null;
  }


  return (
    <div className="tax-invoice-page">
      {/* Tax Invoice Header */}
      <div className="tax-invoice-header">
        <div className="invoice-info">
          <h1 className="invoice-title">Tax Invoice</h1>
          <div className="invoice-info-header">
            Invoice No: <span className="invoice-number"></span>
            {orderDetail.increment_id}
          </div>
          <div className="invoice-info-header">
            Invoice Date: <span className="invoice-number"></span>
            {formatDate(orderDetail.order_date)}
          </div>
        </div>
        <div className="header-logo">
          <img src={logo} className="logo" alt="DROPSHIPZONE" />
        </div>
      </div>

      <div className="tax-invoice-content">
        {/* Two Column Layout - Invoice To & From */}
        <div className="col2-set">
          <div className="column-1">
            <div className="detail-header">Invoice To</div>
            <address>
              {billingInfo.company ? billingInfo.company : ''}
              <br />
              {abn ? `ABN ${abn}` : ''}
              <br />
              {billingInfo.street ? billingInfo.street : ''}
              <br />
              {billingInfo.suburb +
                ' ' +
                (billingInfo.state ?? '') +
                ' ' +
                billingInfo.postcode}
              <br />
              {billingInfo.country === 'AU' ? 'Australia' : billingInfo.country}
            </address>
          </div>
          <div className="column-2">
            <div className="detail-header">Invoice From</div>
            <address>
              New Aim LTD
              <br />
              ABN 50 115 804 432
              <br />
              16-18 CATO ST
              <br />
              HAWTHORN EAST VIC 3123
              <br />
              Australia
            </address>
          </div>
        </div>

        {/* Items Table */}
        <div className="items-section">
          <div className="table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Dropshipzone Order ID</th>
                  <th>PO Number</th>
                  <th>Product Name</th>
                  <th>SKU</th>
                  <th className="a-right">Unit Price</th>
                  <th className="a-right">Shipping Per Item</th>
                  <th className="a-right">Qty</th>
                  <th className="a-right">Amount AUD</th>
                  <th className="a-right">GST</th>
                  <th className="a-right">Order Date</th>
                </tr>
              </thead>
              <tbody>
                {orderDetail.items &&
                  orderDetail.items.map((item, index) => {
                    const isLastItem = index === orderDetail.items.length - 1;
                    return (
                      <tr key={index}>
                        <td>
                          {index == 0 && (
                            <span className="item-data-text">
                              {orderDetail.increment_id}
                            </span>
                          )}
                        </td>
                        <td className="a-left">
                          <span className="item-data-text">{orderDetail.original_customer_order_id ?? ""}</span>
                        </td>
                        <td className="product-name-cell">
                          <span className="item-data-text product-name-text">
                            {item.name}
                          </span>
                        </td>
                        <td className="a-left">
                          <span className="item-data-text">{item.sku}</span>
                        </td>
                        <td className="a-right">
                          <span className="item-data-text">
                            {formatCurrency(item.price)}
                          </span>
                        </td>
                        <td className="a-right">
                          <span className="item-data-text">
                            {formatCurrency(item.shipping_fee)}
                          </span>
                        </td>
                        <td className="a-right">
                          <span className="item-data-text">{item.qty}</span>
                        </td>
                        <td className="a-right">
                          <span className="item-data-text">
                            {formatCurrency(item.sub_total_inc_shipping)}
                          </span>
                        </td>
                        <td className="a-right">
                          <span className="item-data-text">
                            {getOrderItemGST(
                              orderDetail,
                              isLastItem,
                              item.sub_total_inc_shipping
                            )}
                          </span>
                        </td>
                        <td className="a-right">
                          <span className="item-data-text">
                            {formatDateTime(orderDetail.order_date)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                {/* Postage surcharge row */}
                {orderDetail.postage_surcharge > 0 && (
                  <tr>
                    <td></td>
                    <td></td>
                    <td>
                      <span className="item-data-text">Postage Surcharge</span>
                    </td>
                    <td></td>
                    <td className="a-right">
                      <span className="item-data-text">
                        {formatCurrency(
                          OrderPrice.isNZGSTApplicable(orderDetail.country, orderDetail.order_date)
                            ? orderDetail.postage_surcharge / 1.1
                            : orderDetail.postage_surcharge
                        )}
                      </span>
                    </td>
                    <td></td>
                    <td className="a-right">
                      <span className="item-data-text">1</span>
                    </td>
                    <td className="a-right">
                      <span className="item-data-text">
                        {formatCurrency(
                          OrderPrice.isNZGSTApplicable(orderDetail.country, orderDetail.order_date)
                            ? orderDetail.postage_surcharge / 1.1
                            : orderDetail.postage_surcharge
                        )}
                      </span>
                    </td>
                    <td className="a-right">
                      <span className="item-data-text">
                        {OrderPrice.isNZGSTApplicable(orderDetail.country, orderDetail.order_date) || OrderPrice.isGSTExcluded(orderDetail)
                          ? 'Free'
                          : formatCurrency(calculateGST(orderDetail.postage_surcharge))
                        }
                      </span>
                    </td>
                    <td className="a-right">
                      <span className="item-data-text">
                        {formatDateTime(orderDetail.order_date)}
                      </span>
                    </td>
                  </tr>
                )}
                {/* Wholesale pickup handling fee row (AU-only, no NZ ex-GST conversion needed) */}
                {orderDetail.handling_fee > 0 && (
                  <tr>
                    <td></td>
                    <td></td>
                    <td>
                      <span className="item-data-text">Handling Fee</span>
                    </td>
                    <td></td>
                    <td className="a-right">
                      <span className="item-data-text">
                        {formatCurrency(orderDetail.handling_fee)}
                      </span>
                    </td>
                    <td></td>
                    <td className="a-right">
                      <span className="item-data-text">1</span>
                    </td>
                    <td className="a-right">
                      <span className="item-data-text">
                        {formatCurrency(orderDetail.handling_fee)}
                      </span>
                    </td>
                    <td className="a-right">
                      <span className="item-data-text">
                        {OrderPrice.isNZGSTApplicable(orderDetail.country, orderDetail.order_date) || OrderPrice.isGSTExcluded(orderDetail)
                          ? 'Free'
                          : formatCurrency(calculateGST(orderDetail.handling_fee))
                        }
                      </span>
                    </td>
                    <td className="a-right">
                      <span className="item-data-text">
                        {formatDateTime(orderDetail.order_date)}
                      </span>
                    </td>
                  </tr>
                )}
                {/* Wholesale pickup order-level pallet fee row (AU-only) */}
                {orderDetail.pallet_fee > 0 && (
                  <tr>
                    <td></td>
                    <td></td>
                    <td>
                      <span className="item-data-text">Pallet Fee</span>
                    </td>
                    <td></td>
                    <td className="a-right">
                      <span className="item-data-text">
                        {formatCurrency(orderDetail.pallet_fee)}
                      </span>
                    </td>
                    <td></td>
                    <td className="a-right">
                      <span className="item-data-text">1</span>
                    </td>
                    <td className="a-right">
                      <span className="item-data-text">
                        {formatCurrency(orderDetail.pallet_fee)}
                      </span>
                    </td>
                    <td className="a-right">
                      <span className="item-data-text">
                        {OrderPrice.isNZGSTApplicable(orderDetail.country, orderDetail.order_date) || OrderPrice.isGSTExcluded(orderDetail)
                          ? 'Free'
                          : formatCurrency(calculateGST(orderDetail.pallet_fee))
                        }
                      </span>
                    </td>
                    <td className="a-right">
                      <span className="item-data-text">
                        {formatDateTime(orderDetail.order_date)}
                      </span>
                    </td>
                  </tr>
                )}
                {/* Processing fee row */}
                {orderDetail.processing_fee > 0 && (
                  <tr>
                    <td></td>
                    <td></td>
                    <td>
                      <span className="item-data-text">
                        Processing fee - PayPal
                      </span>
                    </td>
                    <td></td>
                    <td className="a-right">
                      <span className="item-data-text">
                        {formatCurrency(orderDetail.processing_fee)}
                      </span>
                    </td>
                    <td></td>
                    <td className="a-right">
                      <span className="item-data-text">1</span>
                    </td>
                    <td className="a-right">
                      <span className="item-data-text">
                        {formatCurrency(orderDetail.processing_fee)}
                      </span>
                    </td>
                    <td className="a-right">
                      <span className="item-data-text">
                        {formatCurrency(calculateGST(orderDetail.processing_fee))}
                      </span>
                    </td>
                    <td className="a-right">
                      <span className="item-data-text">
                        {formatDateTime(orderDetail.order_date)}
                      </span>
                    </td>
                  </tr>
                )}
                <tr className="price-summary-text">
                  <td colSpan="6"></td>
                  <td className="a-left nobr">{isGSTRemoved(orderDetail) && !hasFeeOnlyGST(orderDetail) ? 'Grand Total (excl. GST)' : 'Grand Total (incl. GST)'}</td>
                  <td colSpan="1"></td>
                  <td colSpan="3" className="a-right nobr">
                    <span className="amount-value">
                      {formatCurrency(getGrandTotalInclGST(orderDetail))}
                    </span>
                  </td>
                </tr>
                {(!isGSTRemoved(orderDetail) || hasFeeOnlyGST(orderDetail)) && (
                  <tr className="price-summary-text">
                    <td colSpan="6"></td>
                    <td className="a-left nobr">GST</td>
                    <td colSpan="1"></td>
                    <td colSpan="3" className="a-right nobr">
                      <span className="amount-value">
                        {formatCurrency(getTotalGSTAmount(orderDetail))}
                      </span>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Action Buttons */}
        {OrderPrice.isNZGSTApplicable(
          orderDetail.country,
          orderDetail.order_date
        ) && (
          <div className="note-text">
            *Export sale - GST-free under s38-185 of the GST Act.
          </div>
        )}
        <div className="buttons-set">
          <button
            type="button"
            title="Close Window"
            className="button close-button"
            onClick={() => window.close()}>
            <span>Close Window</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaxInvoicePage;
